import {
  Badge,
  Box,
  Button,
  Circle,
  Divider,
  Flex,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  cycleStatusColor,
  cycleStatusLabel,
  formatCycleName,
  formatDollars,
  formatFullDate,
  formatJsDate,
  statusColor,
} from '~/backup/shared/format';
import {
  awaitingPeriodColor,
  awaitingPeriodLabel,
  currentPeriod,
  formatInputStatuses,
  hasFailedInput,
  todayFullDate,
} from '~/backup/shared/servicingPeriods';
import {
  PortalBusServicingPeriodsQueryGQLType,
  usePortalBusServicingCycleQuery,
  usePortalBusServicingPeriodsQuery,
} from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';
import { Icon } from '~/packages/common-frontend/components/icons';

// -- cycle stepper --

type StepState = 'complete' | 'current' | 'failed' | 'pending';

type CycleStep = {
  label: string;
  detail: string;
  complete: boolean;
  // A step that reached a terminal FAILURE. Distinct from "not complete yet": it
  // stops the stepper advancing and renders red, so a stalled certificate render
  // can't masquerade as one still in flight.
  failed?: boolean;
};

/**
 * Detail line for the "Servicer Review" step — the accept/reject decision on the
 * reconcile result. Reads `approvalStatus` rather than inferring approval from
 * `certificateIssuedAt`: a REJECTED cycle never gets an issue date, so inferring
 * it made a returned cycle render identically to one still awaiting review.
 *
 * `pending` splits two states that look the same but aren't: nothing has been
 * reconciled yet (nothing to review), versus reconciled and sitting on the
 * servicer's desk. The second is "waiting on a human" and should say so.
 */
function reviewStepDetail({
  approvalStatus,
  isReconciled,
  approvedAt,
}: {
  approvalStatus: string;
  isReconciled: boolean;
  approvedAt: number | null | undefined;
}): string {
  if (approvalStatus === 'accepted') return formatJsDate(approvedAt);
  // Deliberately generic: `approvalNote` is the servicer's internal rationale and
  // is not exposed to the portal. What the customer needs is the next action.
  if (approvalStatus === 'rejected') return 'Returned — corrected tape needed';
  return isReconciled ? 'Awaiting servicer review' : '—';
}

/**
 * Detail line for the final "Certificate Generated" step. BUS certificates are
 * pre-signed, so the last thing a customer waits on is the rendered document
 * landing (`certificateStatus=issued`) — there is no signature step after it.
 *
 * A rejected cycle is called out explicitly: no certificate is coming, so a bare
 * dash would read as "still waiting".
 */
function certificateStepDetail({
  certificateStatus,
  approvalStatus,
}: {
  certificateStatus: string | null | undefined;
  approvalStatus: string;
}): string {
  if (certificateStatus === 'issued') return 'Ready to download';
  if (certificateStatus === 'draft') return 'Generating';
  // Deliberately generic: `blockedReason` is an internal renderer diagnostic and is
  // not exposed to the portal. The operator sees the reason in admin.
  if (certificateStatus === 'blocked')
    return 'Generation failed — support notified';
  if (approvalStatus === 'rejected') return 'Not issued for this cycle';
  return '—';
}

/**
 * Label for the certificate download button. A rejected cycle will never produce
 * one, so "Certificate Not Ready" (which implies "yet") would be a lie.
 */
function certificateButtonLabel({
  canDownload,
  approvalStatus,
}: {
  canDownload: boolean;
  approvalStatus: string;
}): string {
  if (canDownload) return 'Download Certificate';
  if (approvalStatus === 'rejected') return 'Certificate Not Issued';
  return 'Certificate Not Ready';
}

type DealPeriod = PortalBusServicingPeriodsQueryGQLType['portalBusServicingPeriods'][number];

/**
 * The stepper for a period nothing has reconciled yet: only the inputs have
 * moved, so the first step carries each one's status and the rest wait.
 */
function awaitingPeriodSteps(period: DealPeriod): CycleStep[] {
  return [
    {
      label: 'Data Received',
      detail: formatInputStatuses(period.inputs),
      complete:
        period.inputs.length > 0 &&
        period.inputs.every((i) => i.status === 'received'),
      failed: hasFailedInput(period),
    },
    { label: 'Reconciled', detail: 'Pending', complete: false },
    { label: 'Servicer Review', detail: '—', complete: false },
    { label: 'Certificate Generated', detail: '—', complete: false },
  ];
}

const STEP_DOT_COLOR: Record<StepState, string> = {
  complete: colors.green[400],
  current: colors.blue[400],
  failed: colors.red[400],
  pending: colors.gray[200],
};

const STEP_DOT_GLYPH: Partial<Record<StepState, string>> = {
  complete: '✓',
  failed: '!',
};

/** Dot diameter; also anchors the connector's offset + vertical centre. */
const STEP_DOT_SIZE = '1.5rem';
/** Breathing room between a connector and the dots on either side of it. */
const STEP_CONNECTOR_GAP = '0.5rem';

function StepDot({ state }: { state: StepState }): React.ReactElement {
  return (
    <Circle
      size={STEP_DOT_SIZE}
      bg={state === 'pending' ? 'transparent' : STEP_DOT_COLOR[state]}
      borderWidth={state === 'pending' ? '2px' : '0'}
      borderColor={colors.gray[200]}
      color="white"
      fontSize="0.85rem"
    >
      {STEP_DOT_GLYPH[state] ?? ''}
    </Circle>
  );
}

function CycleStepper({ steps }: { steps: CycleStep[] }): React.ReactElement {
  // The first not-yet-complete step is the "current" one — unless it FAILED, in
  // which case it is terminal and must not read as work still in progress.
  const currentIndex = steps.findIndex((s) => !s.complete);

  return (
    <Flex align="flex-start" gap="0">
      {steps.map((step, i) => {
        const getState = (): StepState => {
          if (step.complete) return 'complete';
          if (step.failed) return 'failed';
          return i === currentIndex ? 'current' : 'pending';
        };
        const state: StepState = getState();
        const isLast = i === steps.length - 1;
        return (
          <Flex
            key={step.label}
            position="relative"
            flex={isLast ? '0 0 auto' : '1'}
            minW="0"
          >
            <VStack align="flex-start" spacing="0.35rem" minW="0" maxW="100%">
              <StepDot state={state} />
              <Text fontWeight={600} fontSize="0.85rem">
                {step.label}
              </Text>
              <Text color={colors.gray[500]} fontSize="0.75rem">
                {step.detail}
              </Text>
            </VStack>
            {/* Connector spans DOT-TO-DOT, not "end of this label → next dot".
                Every non-last step is an equal-width flex cell with its dot at the
                left edge, so the next dot sits exactly one cell to the right —
                anchoring the line to the cell makes all segments identical. When it
                shared the cell with the label (flex: 1 beside the text), a long
                detail line like "Tape … · MSR …" ate its own segment and the
                stepper looked unevenly clipped. */}
            {!isLast && (
              <Box
                position="absolute"
                top={`calc(${STEP_DOT_SIZE} / 2 - 1px)`}
                left={`calc(${STEP_DOT_SIZE} + ${STEP_CONNECTOR_GAP})`}
                right={STEP_CONNECTOR_GAP}
                height="2px"
                bg={step.complete ? colors.green[400] : colors.gray[200]}
              />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

// -- reconcile checks --

const CHECK_COLOR: Record<string, string> = {
  pass: colors.green[400],
  fail: colors.red[400],
  skipped: colors.gray[400],
};

/** Glyph per outcome. Only names present in our FontAwesome kit render. */
const CHECK_ICON: Record<string, string> = {
  pass: 'circle-check',
  fail: 'circle-xmark',
  skipped: 'circle-info',
};

/**
 * One reconcile check: an outcome glyph + the check name. The operand detail
 * (`dataTape.as_of_date=… vs MSR.collection_period_ending=…`) reads as a formula,
 * so it is opt-in via the card's "Show details" toggle rather than always on.
 */
function CheckRow({
  name,
  status,
  detail,
  showDetail,
}: {
  name: string;
  status: string;
  detail: string;
  showDetail: boolean;
}): React.ReactElement {
  const color = CHECK_COLOR[status] ?? colors.gray[400];
  return (
    <Flex align="baseline" gap="0.75rem" py="0.4rem">
      <Box color={color} fontSize="0.9rem" aria-label={status} title={status}>
        <Icon icon={CHECK_ICON[status] ?? 'circle-info'} />
      </Box>
      <Box>
        <Text fontWeight={500}>{name}</Text>
        {showDetail && detail && (
          <Text color={colors.gray[500]} fontSize="0.8rem">
            {detail}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

// -- small labelled value --

function Field({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}): React.ReactElement {
  return (
    <VStack align="flex-start" spacing="0.15rem">
      <Text
        color={colors.gray[500]}
        fontSize="0.7rem"
        fontWeight={600}
        letterSpacing="0.02em"
      >
        {label}
      </Text>
      <Text fontWeight={500} color={valueColor}>
        {value}
      </Text>
    </VStack>
  );
}

// -- component --

export const DealOverviewView = (): React.ReactElement => {
  // `reportingPeriod` is present on `/deals/:implementationId/periods/:reportingPeriod`
  // (reached from Deliverables) and absent on the deal's own Overview, which
  // shows the current period.
  const { implementationId, reportingPeriod } = useParams<{
    implementationId: string;
    reportingPeriod: string;
  }>();

  // Check operands read as formulas, so they stay opt-in (the concentration-limit
  // drill-down pattern): present, but not in your face by default.
  const [showCheckDetail, setShowCheckDetail] = React.useState(false);

  // The deal's periods, newest first — each scheduled deliverable with its cycle,
  // if one has reconciled. Resolving `reportingPeriod` against *this deal's*
  // periods means an unscheduled date reads as not-found.
  const {
    data: listData,
    loading: listLoading,
    error: listError,
  } = usePortalBusServicingPeriodsQuery({
    variables: { implementationId: implementationId ?? '' },
    skip: !implementationId,
    fetchPolicy: 'cache-and-network',
  });

  const periods = listData?.portalBusServicingPeriods ?? [];
  const today = todayFullDate();
  const current = currentPeriod(periods, today);
  const selected = reportingPeriod
    ? periods.find((p) => p.reportingPeriod === reportingPeriod) ?? null
    : current;
  const isPastPeriod =
    selected !== null &&
    current !== null &&
    selected.reportingPeriod < current.reportingPeriod;

  // The selected period's cycle detail (stepper / reconcile / certificate
  // download). A period still awaiting its tape has no cycle to load.
  const {
    data: detailData,
    loading: detailLoading,
  } = usePortalBusServicingCycleQuery({
    variables: { servicingCycleId: selected?.cycle?.id ?? '' },
    skip: !selected?.cycle,
    fetchPolicy: 'cache-and-network',
  });

  if (
    (listLoading && !listData) ||
    (selected?.cycle && detailLoading && !detailData)
  ) {
    return (
      <Flex justify="center" align="center" minH="20rem">
        <Spinner />
      </Flex>
    );
  }

  const cycle = selected?.cycle
    ? detailData?.portalBusServicingCycle ?? null
    : null;

  if (listError || !selected || (selected.cycle && !cycle)) {
    // An unscheduled `reportingPeriod` on a deal we *can* see is a different miss
    // than a deal with no periods at all, so say which one it is.
    const isMissingPeriod = reportingPeriod !== undefined && periods.length > 0;
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          {isMissingPeriod ? 'Period not found' : 'Deal not found'}
        </Typography>
        <Text color={colors.gray[500]}>
          {isMissingPeriod
            ? 'That servicing period is not scheduled for this deal.'
            : 'This deal has no servicing periods available on your account.'}
        </Text>
        <Link
          as={RouterLink}
          to={`/deals/${implementationId ?? ''}/deliverables`}
          color={colors.blue[400]}
          fontSize="0.9rem"
        >
          View all deliverables →
        </Link>
      </Flex>
    );
  }

  const steps: CycleStep[] = cycle
    ? [
        {
          label: 'Data Received',
          // A BUS cycle reconciles the data tape against the MSR, so both inputs are
          // required before this step completes; surface each leg so a still-pending
          // MSR reads as "MSR —" rather than looking stuck for no visible reason.
          // The inputs follow the latest upload, the cycle the one it reconciled: a
          // failed re-send leaves the cycle as it was, so say so.
          detail: `Tape ${formatJsDate(
            cycle.tapeReceivedAt,
          )} · MSR ${formatJsDate(cycle.msrReceivedAt)}${
            hasFailedInput(selected) ? ' · latest upload failed' : ''
          }`,
          complete:
            cycle.tapeReceivedAt !== null && cycle.msrReceivedAt !== null,
        },
        {
          label: 'Reconciled',
          detail: cycle.reconcileResult
            ? cycle.reconcileResult.status.toUpperCase()
            : 'Pending',
          complete: cycle.reconcileResult !== null,
        },
        // The servicer's accept/reject decision on the reconcile result. Named for the
        // EVENT, not the actor: the viewer here is the lender/originator, while the
        // decision is made on the servicer side — and approval is expected to move out
        // of the admin dashboard. `certificateIssuedAt` is stamped in the same write as
        // `approvedAt` (identical timestamps), and the portal deliberately does not
        // expose the internal approver/note, so it is the date to show.
        {
          label: 'Servicer Review',
          detail: reviewStepDetail({
            approvalStatus: cycle.approvalStatus,
            isReconciled: cycle.reconcileResult !== null,
            approvedAt: cycle.certificateIssuedAt,
          }),
          complete: cycle.approvalStatus === 'accepted',
          failed: cycle.approvalStatus === 'rejected',
        },
        // The certificate is pre-signed, so the last thing a customer waits on is the
        // document being RENDERED (`certificateStatus=issued`), not a signature.
        {
          label: 'Certificate Generated',
          detail: certificateStepDetail({
            certificateStatus: cycle.certificateStatus,
            approvalStatus: cycle.approvalStatus,
          }),
          complete: cycle.certificateStatus === 'issued',
          // A rejected review is terminal for this cycle, so the certificate step can
          // never complete either — mark it failed rather than leaving it "pending"
          // forever behind a step that already stopped.
          failed:
            cycle.certificateStatus === 'blocked' ||
            cycle.approvalStatus === 'rejected',
        },
      ]
    : awaitingPeriodSteps(selected);

  const reconcile = cycle?.reconcileResult ?? null;
  const canDownload = (cycle?.certificateDownloadUrl ?? null) !== null;
  const approvalStatus = cycle?.approvalStatus ?? 'pending';

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="2rem"
    >
      {/* Header. Subtitle names the cycle in view; the facility is the title and
          the deal switcher already names the deal, so the lender's own name and
          the internal implementation name add nothing here. */}
      <Flex justify="space-between" align="baseline" wrap="wrap" gap="0.5rem">
        <Box>
          <Typography variant="page-title" trim>
            {selected.creditFacilityName}
          </Typography>
          <Flex align="baseline" gap="0.6rem" wrap="wrap">
            <Text color={colors.gray[500]} fontSize="0.9rem">
              {formatCycleName(selected.reportingPeriod)}
            </Text>
            {/* Where the cycle stands overall. The stepper below reads out the
                per-phase detail, but "what is this cycle waiting on right now"
                should be answerable from the header alone — and a cycle parked on
                the servicer's desk or returned is otherwise indistinguishable
                from one still processing. Reads `cycleStatus`, not the approval
                decision: an accepted cycle whose certificate render FAILED is
                `blocked`, and badging that as "Approved" would bury the failure.
                A period with no cycle says what it waits on, overdue or not. */}
            <Badge
              color={
                cycle
                  ? cycleStatusColor(cycle.cycleStatus)
                  : awaitingPeriodColor(selected, today)
              }
              fontWeight={600}
              fontSize="0.7rem"
            >
              {cycle
                ? cycleStatusLabel(cycle.cycleStatus)
                : awaitingPeriodLabel(selected, today)}
            </Badge>
            {!cycle && selected.dueDate && (
              <Text color={colors.gray[500]} fontSize="0.8rem">
                Due {formatFullDate(selected.dueDate)}
              </Text>
            )}
            {isPastPeriod && (
              <>
                <Badge colorScheme="gray" fontSize="0.6rem">
                  Past period
                </Badge>
                <Link
                  as={RouterLink}
                  to={`/deals/${implementationId ?? ''}`}
                  fontSize="0.8rem"
                  color={colors.blue[400]}
                  fontWeight={500}
                >
                  Back to current period
                </Link>
              </>
            )}
          </Flex>
        </Box>
        <Button
          as="a"
          href={cycle?.certificateDownloadUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          isDisabled={!canDownload}
          colorScheme="blue"
          size="sm"
        >
          {certificateButtonLabel({ canDownload, approvalStatus })}
        </Button>
      </Flex>

      {/* Cycle stepper */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderColor={colors.gray[100]}
        p="1.5rem"
      >
        <CycleStepper steps={steps} />
      </Box>

      {/* No status/summary row here: cycle + approval status are what the stepper
          above already reads out, and the tape's row count / principal are the
          LOAN COUNT + TOTAL PRINCIPAL in the reconciliation card below, where they
          sit beside the MSR figures they are supposed to tie out against. */}

      {/* Reconciliation */}
      {reconcile && (
        <Box
          borderWidth="1px"
          borderRadius="md"
          borderColor={colors.gray[100]}
          p="1.5rem"
        >
          <Flex justify="space-between" align="baseline" mb="1rem">
            <Typography variant="section-title">Reconciliation</Typography>
            <Flex align="center" gap="1rem">
              <Button
                variant="link"
                size="xs"
                colorScheme="blue"
                fontWeight={500}
                onClick={() => setShowCheckDetail((prev) => !prev)}
              >
                {showCheckDetail ? 'Hide details' : 'Show details'}
              </Button>
              <Text fontWeight={600} color={statusColor(reconcile.status)}>
                {reconcile.status.toUpperCase()}
              </Text>
            </Flex>
          </Flex>

          <SimpleGrid columns={{ base: 2, md: 4 }} gap="1rem" mb="1rem">
            <Field
              label="LOAN COUNT"
              value={reconcile.controlTotals.loanCount.toLocaleString()}
            />
            <Field
              label="TOTAL PRINCIPAL"
              value={formatDollars(reconcile.controlTotals.totalPrincipal)}
            />
            {reconcile.msrValues && (
              <>
                <Field
                  label="MSR ENDING BALANCE"
                  value={formatDollars(reconcile.msrValues.endingPoolBalance)}
                />
                <Field
                  label="SERVICING FEE"
                  value={formatDollars(reconcile.msrValues.servicingFee)}
                />
              </>
            )}
          </SimpleGrid>

          <Divider mb="0.5rem" />

          <VStack align="stretch" spacing="0" divider={<Divider />}>
            {reconcile.checks.map((check) => (
              <CheckRow
                key={check.name}
                name={check.name}
                status={check.status}
                detail={check.detail}
                showDetail={showCheckDetail}
              />
            ))}
          </VStack>

          {showCheckDetail && (
            <Text color={colors.gray[400]} fontSize="0.7rem" mt="1rem">
              {reconcile.provenance}
            </Text>
          )}
        </Box>
      )}

      {/* No cycle list here: the Deliverables tab is that list, and its rows link
          back into this page scoped to the period they name. One list, one place. */}
    </Flex>
  );
};
